import React from 'react';
import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '@/api/baseQuery';
import { TSavedQueryPreset } from '@/features/dashboard/components/DashboardFilterModal/components/PresetChoices/types';
import { UUID } from '@/types';
import { useDatasourceId } from '@/hooks';

export const GET_SAVED_QUERY_PRESETS = 'getSavedQueryPresets';

type TSavedQueryPresetsResponse =
  | { status: 'Ready'; presets: string[] }
  | { status: 'InProgress'; presets: undefined };

export const savedQueryFilterPresetsApi = createApi({
  reducerPath: 'savedQueryFilterPresetsApi',
  baseQuery,
  tagTypes: [GET_SAVED_QUERY_PRESETS],
  endpoints: (builder) => ({
    [GET_SAVED_QUERY_PRESETS]: builder.query<
      TSavedQueryPresetsResponse,
      TSavedQueryPreset & { datasourceId: UUID }
    >({
      providesTags: [GET_SAVED_QUERY_PRESETS],
      query: ({ datasourceId, seriesId, columnName }) =>
        `datasources/${datasourceId}/saved-queries/filter-presets/${seriesId}?columnName=${columnName._name}&columnAlias=${columnName._alias}`,
    }),
  }),
});

export const { useGetSavedQueryPresetsQuery, usePrefetch } =
  savedQueryFilterPresetsApi;

export const invalidateSavedQueryPresetsAction =
  savedQueryFilterPresetsApi.util.invalidateTags([GET_SAVED_QUERY_PRESETS]);

export const getSavedQueryPresets = (savedQueryPreset: TSavedQueryPreset) => {
  const datasourceId = useDatasourceId();
  const [pollingInterval, setPollingInterval] = React.useState(2_000);
  const { data, isFetching, isError, ...rest } = useGetSavedQueryPresetsQuery(
    { ...savedQueryPreset, datasourceId },
    { pollingInterval },
  );

  const status = data?.status;

  React.useEffect(() => {
    if (status === 'Ready' || isError) setPollingInterval(0);
  }, [status, isError]);

  return {
    data: data?.presets,
    isFetching: pollingInterval !== 0 || isFetching,
    isError,
    ...rest,
  } as const;
};
